/** @jsxImportSource @emotion/react */
import type { Editor } from '@tiptap/react';
import { useState, useMemo, useRef, useEffect } from 'react';

import Icon from '../components/Icon';
import IcoArrowDropDownRounded from 'components/icon/icons/IcoArrowDropDownRounded';

import PyMuiMenu from '../components/generalComponents/PyMuiMenu';
import PyMuiMenuItem from '../components/generalComponents/PyMuiMenuItem';
import { styled } from '@mui/material/styles';
import { PyMuiButton } from '../components/generalComponents';
import { DEFAULT_FONT_SIZES, DEFAULT_SIZE } from 'components/editor/grid/reduxStore/defaultTextDefaultValue';
import { useAppSelector } from 'components/editor/grid/reduxStore/Store';
import { selectTableTextDefaultStyles, selectTextDefaultStyles } from 'components/editor/grid/reduxStore/textDefaultStylingSlice';
import { useAttributeSync } from '../hooks/useAttributeSync.ts';

const isActive = (editor: Editor | null, fontSize: string) => editor?.isActive('fontSize', { fontSize }) ?? false;

export const extractNumericValue = (fontSize: string | undefined): string => {
  // Empty string means mixed font sizes in the selection — show blank, like FontFamily does.
  if (fontSize === '') return '';
  if (!fontSize) return DEFAULT_SIZE;
  const match = fontSize.match(/^(\d+)/);
  return match ? match[1] : DEFAULT_SIZE;
};

const FontSizeContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  alignSelf: 'stretch',
});

const FontSizeInput = styled('input')({
  border: 'none',
  background: 'transparent',
  outline: 'none',
  width: '100%',
  textAlign: 'center',
  fontSize: 'inherit',
  fontFamily: 'inherit',
  color: 'inherit',
  padding: 0,
  margin: 0,
});

const InputWrapper = styled('div')({
  width: '50px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

type Props = {
  editor: Editor | null;
  disabled?: boolean;
};

const FontSize = ({ editor, disabled = false }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const textDefaultStyles = useAppSelector(selectTextDefaultStyles);
  const tableDefaultStyles = useAppSelector(selectTableTextDefaultStyles);
  const defaultStyles = useMemo(() => ({ ...textDefaultStyles, ...tableDefaultStyles }), [textDefaultStyles, tableDefaultStyles]);
  const [selectedSize, setSelectedSize] = useAttributeSync(editor, defaultStyles, 'fontSize', `${DEFAULT_SIZE}px`);
  const inputRef = useRef<HTMLInputElement>(null);

  const currentNumericValue = useMemo(() => extractNumericValue(selectedSize), [selectedSize]);
  const [inputValue, setInputValue] = useState(currentNumericValue);

  useEffect(() => {
    setInputValue(currentNumericValue);
  }, [currentNumericValue]);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelectFontSize = (name: string, value: string) => {
    editor?.chain().focus()[value ? 'setFontSize' : 'unsetFontSize'](value).run();
    setSelectedSize(value);
    handleClose();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!value.includes('-')) {
      setInputValue(value);
    }
  };

  const handleChangeFontSize = () => {
    const value = inputValue.trim();
    const numericValue = value.match(/^\d+$/);

    if (!numericValue) {
      setInputValue(currentNumericValue);
      return;
    }

    let size = parseInt(value, 10);
    if (size < 1) size = 1;
    if (size > 400) size = 400;

    const fontSizeValue = `${size}px`;
    editor?.chain().focus().setFontSize(fontSizeValue).run();
    setSelectedSize(fontSizeValue);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleChangeFontSize();
    }
  };

  const handleButtonClick = (event: React.MouseEvent<HTMLElement>) => {
    if (event.target !== inputRef.current && !inputRef.current?.contains(event.target as Node)) {
      handleOpenMenu(event);
    }
  };

  return (
    <FontSizeContainer>
      <PyMuiButton
        testId="font-size-button"
        disabled={disabled}
        isMenuOpen={Boolean(anchorEl)}
        onClickAction={handleButtonClick}
        isActive={selectedSize ? isActive(editor, selectedSize) : false}
        width={58}
      >
        <InputWrapper>
          <FontSizeInput
            ref={inputRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleInputKeyDown}
            onBlur={handleChangeFontSize}
            disabled={disabled}
          />
        </InputWrapper>
        <Icon>
          <IcoArrowDropDownRounded />
        </Icon>
      </PyMuiButton>

      <PyMuiMenu
        testId="font-size-menu"
        id="select-font-family-menu"
        menuListProps="select-font-size-button"
        anchorEl={anchorEl}
        isMenuOpen={Boolean(anchorEl)}
        handleClose={handleClose}
      >
        {DEFAULT_FONT_SIZES.map(({ name, value }) => (
          <PyMuiMenuItem
            key={name}
            testId={`font-size-menu-item-${value}`}
            onClick={() => handleSelectFontSize(name, value)}
            isActive={selectedSize === value}
            menuItem={name}
            customStyle={{ minWidth: 58 }}
          />
        ))}
      </PyMuiMenu>
    </FontSizeContainer>
  );
};

export default FontSize;
